import { OpenInBrowser } from '@mui/icons-material';
import RemoveIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { IconButton, TextField } from '@mui/material';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { Fragrance } from '~/api/api.declaration';
import { AppRoutes } from '~/app/app.routes';
import { BrandService } from '~/service/service.brand';
import { FragranceService } from '~/service/service.fragrance';
import { IngredientService } from '~/service/service.ingredient';
import { NoteService } from '~/service/service.note';
import { OlfactiveService } from '~/service/service.olfactive';
import { PerfumerService } from '~/service/service.perfumer';
import { FragranceCreateDto } from '~/shared/dto/admin/fragrance.dto';
import { FragranceId } from '~/shared/dto/id';
import { Gender } from '~/shared/dto/model.enums';
import { Form } from '~/shared/form';
import { useService } from '~/shared/service/service.base';
import { FieldAutocomplete, FieldAutocompleteSingle } from '~/view/fields/field.autocomplete';
import { FieldEnum } from '~/view/fields/field.enum';
import { FieldNumber } from '~/view/fields/field.number';
import { FieldMultiSelect } from '~/view/fields/field.select';
import { FieldSwitch } from '~/view/fields/field.switch';
import { FieldText } from '~/view/fields/field.text';
import { FieldTextArray } from '~/view/fields/field.textarray';
import { Col, Row } from '~/view/view.box';

type FragranceGeneralProps = {
	id: FragranceId;
	form: Form<FragranceCreateDto, Fragrance>;
};

export const FragranceGeneral = observer<FragranceGeneralProps>(({ id, form }) => {
	const [fragrances, brands, olfactives, perfumers, notes, ingredients] = useService(
		FragranceService,
		BrandService,
		OlfactiveService,
		PerfumerService,
		NoteService,
		IngredientService,
	);

	return (
		<>
			<Row gap={4}>
				<FieldNumber disabled={!!id} field={form.$id} InputProps={{ endAdornment: <Col height={40} /> }} />
				<TextField
					fullWidth
					label={`Super fragrance ${form.initial?.superName ?? ''}`}
					disabled={!!form.initial?.superId}
					value={fragrances.superIdStr ?? ''}
					onChange={e => fragrances.setSuperId(e.target.value)}
					sx={{ backgroundColor: form.initial?.superId ? '#ff000020' : undefined }}
					helperText={
						form.initial?.superSetBy
							? `Set by ${form.initial.superSetBy} at ${dayjs(form.initial.superSetAt).format(
									'YYYY-MM-DD HH:mm',
								)}`
							: undefined
					}
					InputProps={{
						endAdornment: form.initial?.superId ? (
							<IconButton
								onClick={() => fragrances.removeSuperId(id).done(() => window.location.reload())}
							>
								<RemoveIcon />
							</IconButton>
						) : (
							<IconButton
								disabled={!fragrances.superId}
								onClick={() => fragrances.postSuperId(id).done(() => window.location.reload())}
							>
								<SaveIcon />
							</IconButton>
						),
						startAdornment: form.initial?.superId && (
							<IconButton
								onClick={() =>
									window.open(
										AppRoutes.FragranceEdit.buildPath({
											id: form.initial?.superId ?? 0,
										}),
										'_blank',
									)
								}
							>
								<OpenInBrowser />
							</IconButton>
						),
					}}
				/>
			</Row>
			<FieldText field={form.$name} />
			<FieldText field={form.$description} multiline />
			<FieldTextArray field={form.$synonyms} multiline rows={undefined} />
			<FieldAutocompleteSingle
				items={brands.names}
				valueProp="id"
				labelProp="name"
				enabledProp="active"
				field={form.$brandId}
			/>
			<Row gap={4}>
				<FieldEnum field={form.$gender} enumeration={Gender} enumName="Gender" nullLabel="" />
				<FieldMultiSelect items={fragrances.years} valueProp="id" labelProp="name" field={form.$years} />
			</Row>
			<FieldAutocomplete items={olfactives.names} valueProp="id" labelProp="name" field={form.$olfactives} />
			<FieldAutocomplete
				items={perfumers.names}
				valueProp="id"
				labelProp="name"
				enabledProp="active"
				field={form.$perfumers}
			/>
			<FieldAutocomplete
				items={notes.names}
				valueProp="id"
				labelProp="name"
				enabledProp="active"
				field={form.$brandBaseNotes}
			/>
			<FieldAutocomplete
				items={notes.names}
				valueProp="id"
				labelProp="name"
				enabledProp="active"
				field={form.$brandMidNotes}
			/>
			<FieldAutocomplete
				items={notes.names}
				valueProp="id"
				labelProp="name"
				enabledProp="active"
				field={form.$brandTopNotes}
			/>
			<FieldAutocomplete items={ingredients.names} valueProp="id" labelProp="name" field={form.$ingredients} />
			<FieldText multiline field={form.$comment} />
			<FieldSwitch field={form.$active} />
			<FieldSwitch field={form.$discontinued} />
		</>
	);
});
